import { faArrowLeft, faBan, faBell, faCheck, faDownload, faEdit, faEraser, faEye, faEyeSlash, faGlobe, faSave, faSpinner, faSquare } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from '@mui/material/Tooltip';
import ModalWidget from "./ModalWidget";
import { useEffect, useMemo, useState } from "react";
import IS_EFAS from "../../../../components/sitebase/Environment";
import { getCSRFToken } from "../../../../components/sitebase/components/CookieHelper";
import { AoiData } from "../../Map.types";
import LoadingSpinner from "../../../../components/loadingSpinner/LoadingSpinner";

interface UserNotificationData {
    aoi_id: string
    aoi_name: string
    notification_seen: boolean
    product_id: string
    product_time: string
    user_id: string
}

interface GFMUserApiData {
    user_aoi: { aois: AoiData[] }
    user_id: string
    user_notifications: UserNotificationData[]
    user_token: string
}

function AoiRow(props: { aoi: AoiData, onGfmShowAoi: (aoi: AoiData) => void, onGfmHideAoi: (aoi: AoiData) => void }) {
    const [notificationActive, setNotificationActive] = useState<boolean>(props.aoi.twitter_notification_active);
    return <tr key={props.aoi.aoi_id}>
        <td style={{ maxWidth: "8rem", overflow: "auto" }}>{props.aoi.aoi_name}</td>
        <td style={{ padding: "0" }} onClick={() => props.onGfmShowAoi(props.aoi)}><div role="button" className="btn" title="draw square" ><FontAwesomeIcon icon={faEye} /></div></td>
        <td style={{ padding: "0" }} onClick={() => props.onGfmHideAoi(props.aoi)}><div role="button" className="btn" title="hide square"><FontAwesomeIcon icon={faEyeSlash} /></div></td>
        <td style={{ padding: "0" }}><div role="button" className="btn" title="edit on GFM"><a target="_blank " href="https://portal.gfm.eodc.eu/" className="no-ext-arrow m-0"><FontAwesomeIcon icon={faEdit} /></a></div></td>
        <td style={{ padding: "0" }} onClick={() => {
            document.body.style.cursor = "wait";
            fetch(IS_EFAS ? '/api/gfm/aoi-notification-update/' : '/api/v1/gfm-aoi-notification-update/', {
                method: IS_EFAS ? "PUT" : "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    ...(!IS_EFAS && { "X-CSRFToken": getCSRFToken() }),
                },
                body: JSON.stringify({
                    aoi_id: props.aoi.aoi_id,
                    notification_active: !props.aoi.twitter_notification_active,
                    twitter_notification_active: !props.aoi.twitter_notification_active
                })
            }).then(response => {
                if (response.ok) {
                    props.aoi.twitter_notification_active = !props.aoi.twitter_notification_active;
                    setNotificationActive(props.aoi.twitter_notification_active);
                }
            }).finally(() => {
                document.body.style.cursor = "default";
            });
        }}>
            <div key={notificationActive + ""} role="button" className="btn" title={notificationActive ? "Enabled" : "Disabled"}>
                <FontAwesomeIcon icon={notificationActive ? faCheck : faBan} />
            </div>
        </td>
    </tr>
}
function AoiTable(props: {
    aois: AoiData[],
    onGfmShowAoi: (aoi: AoiData) => void,
    onGfmHideAoi: (aoi: AoiData) => void,
}) {

    return <tbody>
        {
            props.aois.map((aoi) => {
                return <AoiRow aoi={aoi} onGfmShowAoi={props.onGfmShowAoi} onGfmHideAoi={props.onGfmHideAoi} />
            })
        }
    </tbody>;
}

function UserNotificationTableRow(props: { notification: UserNotificationData }) {
    const [loading, setLoading] = useState<boolean>(false);

    return <tbody><tr>
        <td>{props.notification.product_time}</td>
        <td><div
            role="button"
            onClick={() => {
                setLoading(true);
                fetch(`${IS_EFAS ? '/api/gfm' : '/api/v1/gfm'}/aoi-product-download/?product=${props.notification.product_id}`)
                    .then(response => response.json())
                    .then((data: { download_link: string }) => {
                        window.open(data.download_link, "_blank");
                        fetch(IS_EFAS ? "/api/gfm/aoi-notification-status/" : "/api/v1/gfm-aoi-notification-status/", {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                ...(!IS_EFAS && { "X-CSRFToken": getCSRFToken() }),
                            },
                            body: JSON.stringify({
                                aoi_id: props.notification.aoi_id,
                                notification_seen: "true",
                                product_id: props.notification.product_id,
                                user_id: props.notification.user_id
                            })
                        }).finally(() => {
                            props.notification.notification_seen = true;
                            setLoading(false)
                        });
                    });
            }}><FontAwesomeIcon className={loading ? "fa-spin" : ""} icon={!loading ? faDownload : faSpinner} /></div>
        </td>
        <td>{!props.notification.notification_seen ||
            <Tooltip title="You have downloaded this product" placement="bottom" arrow disableInteractive>
                <span><FontAwesomeIcon icon={faEye} /></span>
            </Tooltip>}
        </td>
    </tr></tbody>
};

function UserNotificationsTable(props: {
    notifications: UserNotificationData[],
    aois: AoiData[],
    onGfmShowAoi: (aoi: AoiData) => void,
    onGfmHideAoi: (aoi: AoiData) => void,
}) {

    const aoiIdMap = useMemo(() => {
        return props.aois.reduce((acc: Record<string, AoiData>, aoi) => {
            acc[aoi.aoi_id] = aoi;
            return acc;
        }, {});
    }, [props.aois]);

    const groupedByAoiId = useMemo(() => {
        return props.notifications.reduce((acc: Record<string, UserNotificationData[]>, notification) => {
            const key = notification.product_time.split("T")[0];
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(notification);
            return acc;
        }, {});
    }, [props.notifications]);

    return <tbody>
        {Object.entries(groupedByAoiId).map(([aoiDateGroup, notifications], i) => {
            return <tr key={aoiDateGroup + i}>
                <td>
                    <ModalWidget
                        width="40vw"
                        toggleButton={<div role="button" className="btn" title="draw square"><FontAwesomeIcon icon={faArrowLeft} /></div>}
                        title={`Products available for download for ${notifications[0].aoi_name}`}>
                        <div className="mx-auto" style={{ overflowY: "auto", maxHeight: "50vh", width: "45vh" }}>
                            <table className="table border table-hover">
                                {notifications.map((n) => {
                                    return <UserNotificationTableRow key={n.product_id} notification={n} />
                                })}
                            </table>
                        </div>
                    </ModalWidget>
                </td>
                <td className="my-auto"><div className="d-flex flex-column">
                    <div>{notifications[0].aoi_name}</div>
                    <div style={{ fontSize: "12px" }}>{notifications[0].product_time.split("T")[0]}</div></div></td>
                <td onClick={() => props.onGfmShowAoi(aoiIdMap[notifications[0].aoi_id])}><div role="button" className="btn" title="draw square"><FontAwesomeIcon icon={faEye} /></div></td>
                <td onClick={() => props.onGfmHideAoi(aoiIdMap[notifications[0].aoi_id])}><div role="button" className="btn" title="draw square" ><FontAwesomeIcon icon={faEyeSlash} /></div></td>
                <td><div role="button" className="btn" title="edit on GFM"><a target="_blank " href="https://portal.gfm.eodc.eu/" className="no-ext-arrow m-0"><FontAwesomeIcon icon={faEdit} /></a></div></td>
            </tr>
        })}
    </tbody>;
}

export default function GFMWidget(props: {
    onGfmShowAoi: (aoi: AoiData) => void,
    onGfmHideAoi: (aoi: AoiData) => void,
    onGfmStartDrawAoi: () => void,
    onGfmClearDrawAoi: () => void,
    onGfmCreateAoi: (name: string, description: string, onResult: () => void) => void,
    activeDrawing: boolean,
}) {
    const [aoiListVisible, setAoiListVisible] = useState<boolean>(false);
    const [floodNotificationsListVisible, setFloodNotificationsListVisible] = useState<boolean>(false);
    const [creatingAoi, setCreatingAoi] = useState<boolean>(false);

    const [userData, setUserData] = useState<GFMUserApiData | null>(null);

    const fetchData = () => {
        setUserData(null);
        setCreatingAoi(false);
        fetch(IS_EFAS ? "/api/gfm/login/" : "/api/v1/gfm/",
            { credentials: "include" })
            .then(response => response.json())
            .then((data: GFMUserApiData) => {
                console.log("GFM User Data:", data);
                // Handle the user data as needed
                setUserData(data);
            })
            .catch(error => console.error("Error fetching GFM user data:", error));
    };
    useEffect(() => {
        fetchData();
    }, []);

    return (<>
        {!userData && <><LoadingSpinner /><div className="text-center">GFM data is being loaded</div></> ||
            <>
                <div className='d-flex flex-column'>
                    <div role="button" className='btn btn-warning mb-2' onClick={() => setAoiListVisible(!aoiListVisible)}>Areas of Interest <FontAwesomeIcon icon={faSquare} /></div>
                    {aoiListVisible && <div className="d-flex flex-column">
                        <div className="flex-row mx-auto mb-1">
                            <div role="button" className="btn mx-3" title="Draw new AOI" onClick={() => props.onGfmStartDrawAoi()}
                                style={{ boxShadow: props.activeDrawing && "inset 0 0 5px 1px black" || "" }}
                            ><FontAwesomeIcon icon={faSquare} /></div>
                            <div role="button" className="btn mx-3" title="Clear AOI" onClick={() => props.onGfmClearDrawAoi()}><FontAwesomeIcon icon={faEraser} /></div>

                            <ModalWidget toggleButtonText={<FontAwesomeIcon icon={faSave} />} title={"Create AOI"} className="d-inline-block mx-3">
                                <div className="d-flex flex-column">
                                    <input type="text" className="form-control my-2" placeholder="Name" id="gfm-aoi-name" />
                                    <textarea className="form-control my-2" placeholder="Description" id="gfm-aoi-description" />

                                    {creatingAoi && <div className="text-center">Submitting AOI to GFM... This can take up to 30 seconds. <LoadingSpinner /></div>}
                                    <button className="btn btn-warning my-2" disabled={creatingAoi} onClick={() => {
                                        const nameInput = document.getElementById("gfm-aoi-name") as HTMLInputElement;
                                        const descriptionInput = document.getElementById("gfm-aoi-description") as HTMLInputElement;
                                        const name = nameInput.value;
                                        const description = descriptionInput.value;
                                        if (name.length === 0) {
                                            alert("Please enter a name for the AOI");
                                            return;
                                        }
                                        setCreatingAoi(true);
                                        props.onGfmCreateAoi(name, description, () => { nameInput.value = ""; descriptionInput.value = ""; fetchData(); });
                                    }}>Save</button>
                                </div>
                            </ModalWidget>
                        </div>
                        <div style={{ overflowY: "auto", overflowX: "hidden", maxHeight: floodNotificationsListVisible ? "24vh" : "49vh" }}>
                            <table className="table border table-striped table-hover" style={{ verticalAlign: "middle" }}>
                                {userData && <AoiTable key={userData.user_aoi.aois.length} aois={userData.user_aoi.aois} onGfmShowAoi={props.onGfmShowAoi} onGfmHideAoi={props.onGfmHideAoi} />}
                            </table>
                        </div>
                    </div>}

                    <button className='btn btn-warning mb-2' onClick={() => setFloodNotificationsListVisible(!floodNotificationsListVisible)}>Flood notifications <FontAwesomeIcon icon={faBell} /></button>
                    {floodNotificationsListVisible && <div className="d-flex flex-column" style={{ overflowY: "auto", overflowX: "hidden", maxHeight: aoiListVisible ? "25vh" : "50vh" }}>
                        <table className="table border table-striped table-hover">
                            {userData && <UserNotificationsTable key={userData.user_notifications.length} notifications={userData.user_notifications} aois={userData.user_aoi.aois} onGfmShowAoi={props.onGfmShowAoi} onGfmHideAoi={props.onGfmHideAoi} />}
                        </table>
                    </div>}
                    <a target="_blank" href="https://portal.gfm.eodc.eu/" className='btn btn-warning mb-2'>GFM Download <FontAwesomeIcon icon={faGlobe} /></a>
                </div>
                <div>

                </div>
            </>
        }
    </>);
}